import { Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Box, Popover, Pressable } from 'native-base';
import React, { useMemo, useState } from 'react';

import { SignedInNavigationScreenNavigationProp } from '../../../../../../../routes/types';
import { ReportGeneratorListItemNormalized } from '../../../../../../../store/reportGenerator/types';

interface RowActionsProps {
  item: ReportGeneratorListItemNormalized;
  actionLoading: boolean;
  onDownload: () => void;
}

interface MenuActionItem {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

const VerticalDotsLabel: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <Text
    fontSize={Typography['fontsize.lg']}
    lineHeight={Typography['lineheight.sm']}
    color={disabled ? 'neutral.light' : 'neutral.darkest'}
    fontWeight="bold"
    selectable={false}>
    ⋮
  </Text>
);

export const RowActions: React.FC<RowActionsProps> = ({ item, actionLoading, onDownload }) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const [isOpen, setIsOpen] = useState(false);

  const menuItems = useMemo<MenuActionItem[]>(() => {
    const canDownload = item.isDownloadEnabled && !actionLoading;

    return [
      {
        label: 'Detalhes',
        onPress: () => {
          navigation.navigate('ReportGeneratorDetailsModal', { item });
        },
      },
      {
        label: 'Fazer download',
        disabled: !canDownload,
        onPress: () => {
          if (!canDownload) {
            return;
          }
          onDownload();
        },
      },
    ];
  }, [actionLoading, item, navigation, onDownload]);

  const closeMenu = () => setIsOpen(false);

  return (
    <Popover
      useRNModal
      isOpen={isOpen}
      onClose={closeMenu}
      placement="bottom right"
      trigger={(triggerProps) => (
        <Pressable
          {...triggerProps}
          testID="report-generator-row-actions-menu-trigger"
          disabled={actionLoading}
          hitSlop={8}
          px="6px"
          py="2px"
          opacity={actionLoading ? 0.5 : 1}
          onPress={() => setIsOpen(true)}>
          <VerticalDotsLabel disabled={actionLoading} />
        </Pressable>
      )}>
      <Popover.Content
        minW="180px"
        shadow={4}
        borderWidth={1}
        borderColor="neutral.lightest"
        borderRadius="sm"
        bg="neutral.white"
        p={Spacing['inline.nano']}>
        <VStack space="stack.quarck" width="full">
          {menuItems.map((menuItem) => (
            <Pressable
              key={menuItem.label}
              testID={`report-generator-row-action-${menuItem.label}`}
              disabled={menuItem.disabled}
              px={Spacing['inline.quarck']}
              py={Spacing['inset.quarck']}
              borderRadius="sm"
              opacity={menuItem.disabled ? 0.45 : 1}
              _hover={menuItem.disabled ? undefined : { bg: 'neutral.lightest' }}
              onPress={() => {
                if (menuItem.disabled) {
                  return;
                }
                closeMenu();
                menuItem.onPress();
              }}>
              <Text
                fontSize={Typography['fontsize.sm']}
                color={menuItem.disabled ? 'neutral.medium' : 'neutral.darkest'}
                variant="regular"
                selectable={false}>
                {menuItem.label}
              </Text>
            </Pressable>
          ))}
        </VStack>
      </Popover.Content>
    </Popover>
  );
};

export const RowActionsSkeleton: React.FC = () => (
  <Box px="6px" py="2px" alignItems="center" justifyContent="center">
    <VerticalDotsLabel disabled />
  </Box>
);
